export interface Question {
  id: string;
  type: 'multiple-choice' | 'true-false';
  prompt: string;
  options?: string[];
  answer: string | boolean;
  explanation: string;
  references: string[];
}

export interface Quiz {
  quizId: string;
  moduleSlug: string;
  title: string;
  version: string;
  questions: Question[];
}

export const quizBank: Record<string, Quiz> = {
  "kyber-perusteet": {
    quizId: "q-kyber",
    moduleSlug: "kyber-perusteet",
    title: "Kyberperusteiden osaamistesti",
    version: "1.5.0",
    questions: [
      {
        id: "k1",
        type: "multiple-choice",
        prompt: "Mitä CIA-mallin 'I' (Integrity/Eheys) tarkoittaa?",
        options: ["Tieto on vain valtuutettujen saatavilla", "Tieto ei muutu luvattomasti", "Järjestelmä on aina käytettävissä", "Tieto on salattu AES-256:lla"],
        answer: "Tieto ei muutu luvattomasti",
        explanation: "Eheys (Integrity) varmistaa, että tieto pysyy muuttumattomana ja luotettavana.",
        references: ["cia-malli"]
      },
      {
        id: "k2",
        type: "true-false",
        prompt: "MFA on tarpeeton, jos käytössä on yli 20 merkin salasana.",
        answer: false,
        explanation: "Salasana voidaan silti varastaa tai urkkia; MFA tarjoaa toisen suojakerroksen.",
        references: ["mfa"]
      },
      {
        id: "k3",
        type: "multiple-choice",
        prompt: "Mihin luokkaan 'Asiakasrekisteri' tyypillisesti kuuluu tiedon luokittelussa?",
        options: ["Julkinen", "Sisäinen", "Luottamuksellinen", "Salainen"],
        answer: "Luottamuksellinen",
        explanation: "Asiakasdata sisältää henkilötietoja ja on siten luottamuksellista tai salaista.",
        references: ["tiedon-luokittelu"]
      },
      {
        id: "k4",
        type: "true-false",
        prompt: "CIA-mallin 'A' (Availability) tarkoittaa tiedon salausta.",
        answer: false,
        explanation: "Availability (Saatavuus) tarkoittaa, että tieto ja järjestelmät ovat käytettävissä silloin kun niitä tarvitaan.",
        references: ["cia-malli"]
      },
      {
        id: "k5",
        type: "multiple-choice",
        prompt: "Mikä on monivaiheisen tunnistautumisen (MFA) ensisijainen hyöty?",
        options: ["Nopeampi kirjautuminen", "Vähentää muistettavien salasanojen määrää", "Estää luvaton pääsy vaikka salasana vuotaisi", "Parantaa nettiyhteyden nopeutta"],
        answer: "Estää luvaton pääsy vaikka salasana vuotaisi",
        explanation: "MFA vaatii toisen vahvistuksen, jota hyökkääjällä ei ole, vaikka hänellä olisi salasana.",
        references: ["mfa"]
      }
    ]
  },
  "ai-turva": {
    quizId: "q-ai",
    moduleSlug: "ai-safety",
    title: "AI-turvallisuuden testi",
    version: "1.5.0",
    questions: [
      {
        id: "ai1",
        type: "multiple-choice",
        prompt: "Mikä on 'Prompt-injektio'?",
        options: ["Tekoälyn koodin päivitys", "Hyökkäys mallin ohjaamiseksi", "Uuden kielimallin opetus", "Datan salaus"],
        answer: "Hyökkäys mallin ohjaamiseksi",
        explanation: "Prompt-injektio pyrkii manipuloimaan mallin ohjeita syöttämällä sille tekstiä.",
        references: ["prompt-injektio"]
      },
      {
        id: "ai2",
        type: "true-false",
        prompt: "Shadow AI tarkoittaa tekoälyä, joka toimii vain pimeässä huoneessa.",
        answer: false,
        explanation: "Shadow AI tarkoittaa organisaation IT-hallinnon ulkopuolista, valvomatonta tekoälyn käyttöä.",
        references: ["shadow-ai"]
      },
      {
        id: "ai3",
        type: "multiple-choice",
        prompt: "Mikä on paras tapa suojautua deepfake-videohuijauksilta puhelussa?",
        options: ["Lopettaa puhelu välittömästi", "Käyttää sovittua turvasanaa toisessa kanavassa", "Luottaa virustorjuntaan", "Kysyä soittajalta hänen syntymäaikaansa"],
        answer: "Käyttää sovittua turvasanaa toisessa kanavassa",
        explanation: "Organisatorinen varmistus (esim. koodisana) on teknistä tunnistusta luotettavampi.",
        references: ["deepfake"]
      },
      {
        id: "ai4",
        type: "true-false",
        prompt: "Tekoälylle syötetty data voi päätyä osaksi mallin koulutusmateriaalia.",
        answer: true,
        explanation: "Monet julkiset palvelut hyödyntävät syötteitä mallien jatkokoulutukseen, jos sitä ei erikseen estetä.",
        references: ["shadow-ai"]
      },
      {
        id: "ai5",
        type: "multiple-choice",
        prompt: "Miten organisaatio voi parhaiten hallita Shadow AI -riskiä?",
        options: ["Kieltämällä tekoälyn käytön kokonaan", "Määrittelemällä sallitut työkalut ja ohjeistamalla niiden käytön", "Asentamalla palomuurin", "Poistamalla Internet-yhteyden"],
        answer: "Määrittelemällä sallitut työkalut ja ohjeistamalla niiden käytön",
        explanation: "Hallittu käyttö ja selkeät pelisäännöt ovat tehokkain tapa minimoida riskit.",
        references: ["shadow-ai"]
      }
    ]
  },
  "mobiiliturva": {
    quizId: "q-mobile",
    moduleSlug: "mobiiliturva",
    title: "Mobiiliturvallisuuden testi",
    version: "1.5.0",
    questions: [
      {
        id: "m1",
        type: "multiple-choice",
        prompt: "Mikä on 'Juice Jacking'?",
        options: ["Akun ylikuumeneminen", "Tiedonsiirto ja saastuminen julkisen USB-latausportin kautta", "Näytön kaappaus", "Wi-Fi-hyökkäys"],
        answer: "Tiedonsiirto ja saastuminen julkisen USB-latausportin kautta",
        explanation: "USB-liitin välittää sekä virtaa että dataa, mitä hyökkääjät hyödyntävät.",
        references: ["juice-jacking"]
      },
      {
        id: "m2",
        type: "true-false",
        prompt: "PIN-koodin pituudella ei ole merkitystä, jos käytössä on biometria.",
        answer: false,
        explanation: "PIN-koodi on biometrian takaportti ja sen on oltava vahva.",
        references: ["fyysinen-paasy"]
      },
      {
        id: "m3",
        type: "multiple-choice",
        prompt: "Mikä suojaa parhaiten julkisessa Wi-Fi-verkossa?",
        options: ["VPN", "Virustorjunta", "Lentotila", "Näytön lukitus"],
        answer: "VPN",
        explanation: "VPN salaa liikenteen, jolloin sitä ei voida lukea avoimessa verkossa.",
        references: ["juice-jacking"]
      },
      {
        id: "m4",
        type: "true-false",
        prompt: "Kadonneen puhelimen voi tyhjentää etänä, jos se on konfiguroitu oikein.",
        answer: true,
        explanation: "Etsi laitteeni / Find My -palvelut mahdollistavat etätyhjennyksen.",
        references: ["fyysinen-paasy"]
      },
      {
        id: "m5",
        type: "multiple-choice",
        prompt: "Miksi USB Data Blocker on hyödyllinen matkustaessa?",
        options: ["Se lataa nopeammin", "Se estää fyysisesti datalinjat USB-kaapelista", "Se säästää akkua", "Se parantaa kuuluvuutta"],
        answer: "Se estää fyysisesti datalinjat USB-kaapelista",
        explanation: "Data blocker varmistaa, että vain virta kulkee kaapelissa.",
        references: ["juice-jacking"]
      }
    ]
  },
  "tietosuoja": {
    quizId: "q-privacy",
    moduleSlug: "tietosuoja-perusteet",
    title: "Tietosuojan osaamistesti",
    version: "1.5.0",
    questions: [
      {
        id: "p1",
        type: "true-false",
        prompt: "PbD (Privacy by Design) tarkoittaa, että tietosuoja huomioidaan vasta projektin lopussa.",
        answer: false,
        explanation: "PbD tarkoittaa tietosuojan huomioimista heti suunnittelun alusta alkaen.",
        references: ["p2"]
      },
      {
        id: "p2",
        type: "multiple-choice",
        prompt: "Mikä on GDPR:n mukainen aikaraja vakavasta tietomurtoilmoituksesta viranomaiselle?",
        options: ["24h", "48h", "72h", "Viikko"],
        answer: "72h",
        explanation: "Vakavista tietomurroista on ilmoitettava valvontaviranomaiselle 72 tunnin kuluessa.",
        references: ["p1"]
      },
      {
        id: "p3",
        type: "true-false",
        prompt: "Datan minimointi tarkoittaa, että kerätään vain välttämätön tieto tiettyyn tarkoitukseen.",
        answer: true,
        explanation: "Minimointiperiaate on keskeinen osa tietosuojaa ja GDPR:ää.",
        references: ["p2"]
      },
      {
        id: "p4",
        type: "multiple-choice",
        prompt: "Mikä seuraavista EI ole tietosuojan perusperiaate?",
        options: ["Läpinäkyvyys", "Datan myynti ilman lupaa", "Käyttötarkoitussidonnaisuus", "Luottamuksellisuus"],
        answer: "Datan myynti ilman lupaa",
        explanation: "GDPR kieltää perusteettoman datan käytön muuhun kuin ilmoitettuun tarkoitukseen.",
        references: ["p1"]
      },
      {
        id: "p5",
        type: "true-false",
        prompt: "Henkilötietoa on mikä tahansa tieto, josta yksilö voidaan suoraan tai epäsuorasti tunnistaa.",
        answer: true,
        explanation: "Tämä on henkilötiedon laaja määritelmä tietosuojalaissa.",
        references: ["p1"]
      }
    ]
  },
  "salaus": {
    quizId: "q-crypto",
    moduleSlug: "salaus-opas",
    title: "Salauksen osaamistesti",
    version: "1.5.0",
    questions: [
      {
        id: "e1",
        type: "multiple-choice",
        prompt: "Mikä on symmetrisen salauksen keskeinen piirre?",
        options: ["Käytetään kahta eri avainta", "Käytetään samaa avainta salaukseen ja purkuun", "Sitä ei voi purkaa ollenkaan", "Se vaatii aina Internet-yhteyden"],
        answer: "Käytetään samaa avainta salaukseen ja purkuun",
        explanation: "Symmetrinen salaus perustuu yhteen jaettuun avaimeen.",
        references: ["e1"]
      },
      {
        id: "e2",
        type: "true-false",
        prompt: "AES-256 pidetään tällä hetkellä erittäin turvallisena symmetrisenä salausstandardina.",
        answer: true,
        explanation: "AES-256 on teollisuuden standardi vahvalle salaukselle.",
        references: ["e1"]
      },
      {
        id: "e3",
        type: "multiple-choice",
        prompt: "Mikä on asymmetrisen salauksen toinen nimi?",
        options: ["Yksityinen salaus", "Julkisen avaimen salaus", "Piilosalaus", "Kertasalaus"],
        answer: "Julkisen avaimen salaus",
        explanation: "Asymmetrinen salaus käyttää julkista ja yksityistä avainparia.",
        references: ["e1"]
      },
      {
        id: "e4",
        type: "true-false",
        prompt: "Salausavaimia kannattaa säilyttää samassa kansiossa salatun tiedon kanssa.",
        answer: false,
        explanation: "Avainten hallinta vaatii avainten ja datan loogista erottamista turvallisuuden takia.",
        references: ["e1"]
      },
      {
        id: "e5",
        type: "multiple-choice",
        prompt: "Mihin RSA-algoritmia yleensä käytetään digitaalisessa viestinnässä?",
        options: ["Videon pakkaukseen", "Avainten vaihtoon ja digitaalisiin allekirjoituksiin", "Näytönohjaimen ohjaukseen", "Sähköpostin lähetykseen"],
        answer: "Avainten vaihtoon ja digitaalisiin allekirjoituksiin",
        explanation: "RSA on suosittu asymmetrinen algoritmi tunnistautumiseen ja avainten vaihtoon.",
        references: ["e1"]
      }
    ]
  },
  "routines": {
    quizId: "q-routines",
    moduleSlug: "routines",
    title: "Arjen rutiinien testi",
    version: "1.5.0",
    questions: [
      {
        id: "r1",
        type: "true-false",
        prompt: "Viikoittainen päivitysten tarkistus on turhaa automaation takia.",
        answer: false,
        explanation: "Automaatio voi pettää; manuaalinen varmistus on osa hyvää rutiinia.",
        references: ["viikko"]
      },
      {
        id: "r2",
        type: "multiple-choice",
        prompt: "Kuinka usein varmuuskopioiden palautus tulisi testata käytännössä?",
        options: ["Ei koskaan", "Kerran vuodessa", "Säännöllisesti (esim. 3kk välein)", "Vasta kun ongelma ilmenee"],
        answer: "Säännöllisesti (esim. 3kk välein)",
        explanation: "Varmuuskopio on arvoton, jos sitä ei voida palauttaa onnistuneesti.",
        references: ["viikko"]
      },
      {
        id: "r3",
        type: "true-false",
        prompt: "Turhien sovellusten poistaminen pienentää hyökkäyspinta-alaa.",
        answer: true,
        explanation: "Vähemmän koodia tarkoittaa vähemmän potentiaalisia haavoittuvuuksia.",
        references: ["kuukausi"]
      },
      {
        id: "r4",
        type: "multiple-choice",
        prompt: "Mikä on kriittisin toimi heti maanantai-aamuna?",
        options: ["Juoda kahvia", "Tarkistaa päivitykset ja varmuuskopiot", "Lukea uutiset", "Vastata kaikkiin sähköposteihin"],
        answer: "Tarkistaa päivitykset ja varmuuskopiot",
        explanation: "Viikon aloitus on paras hetki varmistaa digitaalinen kunto.",
        references: ["viikko"]
      },
      {
        id: "r5",
        type: "true-false",
        prompt: "Salasanaohjelman terveyden tarkistus kuuluu kuukausittaiseen huoltoon.",
        answer: true,
        explanation: "Vanhentuneet tai heikot salasanat on hyvä seuloa säännöllisesti.",
        references: ["kuukausi"]
      }
    ]
  },
  "sertifikaatti": {
    quizId: "q-master",
    moduleSlug: "master-certification",
    title: "Arctic Security Certification - Loppukoe",
    version: "2.0.0",
    questions: [
      {
        id: "m1",
        type: "multiple-choice",
        prompt: "Mikä on tehokkain tapa suojautua deepfake-videopuhelulta yrityksessä?",
        options: ["Sammuttaa kamera", "Käyttää sovittua turvasanaa toisessa kanavassa", "Luottaa virustorjuntaan", "Päivittää selain"],
        answer: "Käyttää sovittua turvasanaa toisessa kanavassa",
        explanation: "Organisatorinen varmistus (esim. koodisana) on teknistä tunnistusta luotettavampi.",
        references: ["m1"]
      },
      {
        id: "m2",
        type: "true-false",
        prompt: "GDPR vaatii ilmoituksen tietosuojavaltuutetulle 72 tunnin kuluessa tietomurrosta.",
        answer: true,
        explanation: "Tämä on lakisääteinen aikaraja vakavissa tapauksissa.",
        references: ["m1"]
      },
      {
        id: "m3",
        type: "multiple-choice",
        prompt: "Mikä CIA-mallin osa varmistaa, että tieto ei muutu luvattomasti?",
        options: ["Confidentiality", "Integrity", "Availability", "Authentication"],
        answer: "Integrity",
        explanation: "Eheys takaa tiedon oikeellisuuden.",
        references: ["m1"]
      },
      {
        id: "m4",
        type: "true-false",
        prompt: "Prompt-injektio voi pakottaa tekoälyn paljastamaan salaisuuksia.",
        answer: true,
        explanation: "Injektiolla voidaan kiertää mallin suojat ja ohjeet.",
        references: ["m1"]
      },
      {
        id: "m5",
        type: "multiple-choice",
        prompt: "Mobiiliturvassa 'Juice Jacking' tapahtuu minkä kautta?",
        options: ["Wi-Fi", "Bluetooth", "USB-latausportti", "SMS"],
        answer: "USB-latausportti",
        explanation: "Datalataus USB:n kautta mahdollistaa hyökkäyksen.",
        references: ["m1"]
      }
    ]
  }
};
